import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import teacherService from '../../services/api/teacher';
import { 
  Plus, 
  Trash2, 
  Edit3, 
  Archive,
  Award,
  BookOpen,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  BarChart3,
  Search,
  ChevronLeft,
  ChevronRight,
  Filter,
  Zap,
  LayoutDashboard,
  ShieldCheck,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const QuizzesManagement = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // جلب بيانات الاختبارات (المنطق الأصلي كاملاً)
  const { data: quizzesData, isLoading, error } = useQuery(
    'teacher-quizzes',
    () => teacherService.getTeacherQuizzes(),
    { retry: 1 }
  );

  const quizzes = quizzesData?.quizzes || [];

  // Mutation لحذف اختبار (المنطق الأصلي)
  const deleteQuizMutation = useMutation(
    (quizId) => teacherService.deleteQuiz(quizId),
    {
      onSuccess: () => {
        toast.success('تم حذف الاختبار من النظام بنجاح');
        queryClient.invalidateQueries('teacher-quizzes');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في حذف الاختبار');
      }
    }
  );

  // منطق تصفية البحث (المنطق الأصلي كاملاً)
  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         quiz.lesson_title?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'published' && quiz.is_published) ||
                         (filterStatus === 'draft' && !quiz.is_published) ||
                         (filterStatus === 'active' && quiz.is_active) ||
                         (filterStatus === 'inactive' && !quiz.is_active);
    
    return matchesSearch && matchesFilter;
  });

  // منطق الترقيم (Pagination Logic)
  const totalPages = Math.ceil(filteredQuizzes.length / itemsPerPage);
  const startIdx = (currentPage - 1) * itemsPerPage;
  const paginatedQuizzes = filteredQuizzes.slice(startIdx, startIdx + itemsPerPage);

  const handleDelete = (quizId, title) => {
    if (window.confirm(`⚠️ تحذير: هل أنت متأكد من حذف الاختبار "${title}" نهائياً؟ سيتم حذف كافة إجابات الطلاب المرتبطة به.`)) {
      deleteQuizMutation.mutate(quizId);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري جلب بنك الاختبارات...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 px-6">
        <div className="bg-white rounded-[2.5rem] shadow-xl p-10 border border-red-100 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-black text-gray-900 mb-2">تعذر تحميل البيانات</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، واجهنا مشكلة أثناء محاولة الاتصال بالخادم لجلب قائمة الاختبارات.</p>
          <button
            onClick={() => window.location.reload()}
            className="w-full px-8 py-3 bg-red-600 text-white rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-100"
          >
            إعادة المحاولة الآن
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8 min-h-screen bg-gray-50/30">
      
      {/* Header & Main Actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
          <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
            <div className="bg-primary-600 text-white p-2 rounded-2xl shadow-lg shadow-primary-200">
                <LayoutDashboard size={28} />
            </div>
            إدارة الاختبارات
          </h1>
          <p className="text-gray-500 font-medium flex items-center gap-2">
            <ShieldCheck className="text-emerald-500" size={18} />
            تحكم في تقييمات طلابك، أنشئ أسئلة تفاعلية وتابع النتائج
          </p>
        </div>

        <button
          onClick={() => navigate('/teacher/quizzes/create')}
          className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all flex items-center justify-center gap-2 transform active:scale-95"
        >
          <Plus size={22} /> إنشاء اختبار جديد
        </button>
      </div>

      {/* Stats Quick Dashboard */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {[
          { label: 'إجمالي الاختبارات', value: quizzes.length, color: 'text-blue-600', bg: 'bg-blue-50', icon: Award },
          { label: 'اختبارات منشورة', value: quizzes.filter(q => q.is_published).length, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: CheckCircle2 },
          { label: 'مسودات (Drafts)', value: quizzes.filter(q => !q.is_published).length, color: 'text-amber-600', bg: 'bg-amber-50', icon: FileText },
          { label: 'اختبارات نشطة', value: quizzes.filter(q => q.is_active).length, color: 'text-purple-600', bg: 'bg-purple-50', icon: Zap }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex items-center gap-5 group hover:shadow-md transition-all">
            <div className={`p-4 ${stat.bg} ${stat.color} rounded-2xl group-hover:scale-110 transition-transform`}>
              <stat.icon size={24} />
            </div>
            <div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">{stat.label}</p>
              <p className="text-2xl font-black text-gray-900 leading-none">{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters & Search Toolbar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center">
          <div className="flex-1 relative group w-full">
            <Search className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              placeholder="ابحث عن اختبار محدد، أو درس مرتبط..."
              className="w-full pr-14 pl-6 py-4 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
            />
          </div>
          
          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="bg-gray-50 p-1.5 rounded-2xl border border-gray-100 flex items-center flex-1 lg:flex-none">
                <div className="p-2.5 text-gray-400"><Filter size={18} /></div>
                <select
                    value={filterStatus}
                    onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}
                    className="bg-transparent px-4 py-2 font-black text-xs text-gray-600 focus:outline-none appearance-none cursor-pointer"
                >
                    <option value="all">كل الحالات</option>
                    <option value="published">المنشور فقط</option>
                    <option value="draft">المسودات</option>
                    <option value="active">الحالة: نشط</option>
                    <option value="inactive">الحالة: معطل</option>
                </select>
                <ChevronLeft size={16} className="text-gray-300 ml-2 -rotate-90" />
            </div>

            {(searchTerm || filterStatus !== 'all') && (
                <span className="text-[10px] font-black text-primary-600 bg-primary-50 px-4 py-2 rounded-xl border border-primary-100 uppercase tracking-widest whitespace-nowrap">
                    نتائج: {filteredQuizzes.length}
                </span>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        {paginatedQuizzes.length === 0 ? (
          /* Enhanced Empty State */
          <div className="py-32 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Archive size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد سجلات لعرضها</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">نعتذر، لم نجد أي اختبارات تطابق معايير البحث الحالية أو أنك لم تقم بإنشاء اختبارات بعد.</p>
            {quizzes.length === 0 && (
                <button 
                    onClick={() => navigate('/teacher/quizzes/create')}
                    className="mt-8 bg-primary-600 text-white px-10 py-4 rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 flex items-center gap-2 mx-auto"
                >
                    <Plus size={20} /> ابدأ بإنشاء أول اختبار
                </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead className="bg-gray-50/50">
                <tr className="border-b border-gray-100">
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">تفاصيل الاختبار</th>
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">الدرس المرتبط</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">بنك الأسئلة</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">الزمن المتاح</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">حالة النشر</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">الإجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {paginatedQuizzes.map((quiz) => (
                  <tr key={quiz.id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex flex-col">
                        <div className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors truncate max-w-xs">{quiz.title}</div>
                        <div className="flex items-center gap-2 mt-1.5">
                          {quiz.is_published ? (
                            <span className="flex items-center gap-1 text-[9px] font-black text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded uppercase tracking-tighter border border-emerald-100">
                              <CheckCircle2 size={10} /> تم النشر للطلاب
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-[9px] font-black text-amber-600 bg-amber-50 px-2 py-0.5 rounded uppercase tracking-tighter border border-amber-100">
                              <FileText size={10} /> مسودة داخلية
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shadow-sm">
                            <BookOpen size={20} />
                        </div>
                        <div className="text-sm font-bold text-gray-700 truncate max-w-[180px]">{quiz.lesson_title || 'غير محدد'}</div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <div className="inline-flex flex-col items-center">
                          <span className="text-base font-black text-purple-700 bg-purple-50 w-10 h-10 flex items-center justify-center rounded-xl border border-purple-100 shadow-sm">
                            {quiz.total_questions}
                          </span>
                          <span className="text-[8px] font-black text-gray-400 uppercase mt-1 tracking-widest">سؤال</span>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <div className="flex flex-col items-center">
                        <div className="flex items-center gap-1.5 text-sm font-black text-gray-800">
                            <Clock size={14} className="text-primary-500" />
                            {quiz.duration} <span className="text-[10px] text-gray-400 font-bold">دقيقة</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border ${
                        quiz.is_active 
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}>
                        {quiz.is_active ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {quiz.is_active ? 'نشط' : 'معطل'}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-left">
                      <div className="flex items-center justify-end gap-3 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        <button
                          onClick={() => navigate(`/teacher/quizzes/${quiz.id}/results`)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-purple-600 rounded-xl hover:bg-purple-600 hover:text-white transition-all shadow-sm transform active:scale-90" 
                          title="عرض النتائج والتقارير"
                        >
                          <BarChart3 size={18} />
                        </button>
                        <button
                          onClick={() => navigate(`/teacher/quizzes/${quiz.id}/edit`)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm transform active:scale-90"
                          title="تعديل الاختبار"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(quiz.id, quiz.title)}
                          className="w-10 h-10 flex items-center justify-center bg-white border border-rose-100 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm transform active:scale-90"
                          title="حذف نهائي"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Improved Pagination Footer */}
        {totalPages > 1 && (
          <div className="bg-gray-50/50 border-t border-gray-100 px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                عرض الصفحة <span className="text-primary-600 font-black">{currentPage}</span> من <span className="text-gray-900 font-black">{totalPages}</span> صفحات
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-3 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronRight size={20} />
              </button>
              
              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                   /* إظهار الصفحات القريبة فقط إذا كان العدد كبيراً */
                   (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) && (
                    <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-xl font-black text-sm transition-all duration-300 ${
                        currentPage === page
                            ? 'bg-primary-600 text-white shadow-lg shadow-primary-100'
                            : 'bg-white text-gray-500 border border-gray-100 hover:bg-primary-50 hover:text-primary-600'
                        }`}
                    >
                        {page}
                    </button>
                   )
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-3 bg-white border border-gray-100 rounded-xl text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm"
              >
                <ChevronLeft size={20} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Quick Tips Footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-3xl -z-0 translate-x-1/2 -translate-y-1/2 group-hover:scale-110 transition-transform duration-700"></div>
        <div className="relative z-10 text-center md:text-right space-y-2">
            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight tracking-tighter uppercase">
                <Sparkles className="text-primary-500" /> هل تود زيادة تفاعل طلابك؟
            </h3>
            <p className="text-gray-400 font-medium max-w-xl">الاختبارات الدورية القصيرة (Quizzes) تزيد من معدل بقاء الطالب في الكورس بنسبة ٤٠٪. قم بإنشاء اختبار لكل درس اليوم.</p>
        </div>
        <button 
            onClick={() => navigate('/teacher/reports')}
            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-colors transform active:scale-95"
        >
            عرض تقارير التفاعل <ArrowRight size={20} className="rotate-180" />
        </button>
      </div>
    </div>
  );
};

export default QuizzesManagement;